import React, { useMemo } from 'react';

import { LazyLinkPicker } from '@atlaskit/link-picker/lazy';
import type { LinkPickerPlugin, LinkPickerProps } from '@atlaskit/link-picker/types';
import { useLinkPickerPlugins } from '@atlassian/link-picker-plugins/use-link-picker-plugins';

import { useProductDependencies } from '../../../controllers/use-product-dependencies';

type OnSubmitPayload = Parameters<LinkPickerProps['onSubmit']>[0];

type LinkPickerContentProps = {
	cloudId: string;
	link: OnSubmitPayload;
	onLinkSelect: (selectedLink: OnSubmitPayload) => void;
	setIsOpen(isOpen: boolean): void;
};

const supportedPluginKeys = ['confluence', 'jira', 'atlas', 'loom'];

export const LinkPickerContent = ({
	cloudId,
	link,
	setIsOpen,
	onLinkSelect,
}: LinkPickerContentProps): React.JSX.Element => {
	const { linkPickerConfig } = useProductDependencies();

	const handleSubmit: LinkPickerProps['onSubmit'] = (payload) => {
		onLinkSelect(payload);
		setIsOpen(false);
	};

	const handleCancel: LinkPickerProps['onCancel'] = () => {
		setIsOpen(false);
	};

	const { isLoading, plugins } = useLinkPickerPlugins({
		cloudId,
		product: 'confluence',
	});

	const filteredPlugins = useMemo(() => {
		return plugins.filter((plugin: LinkPickerPlugin) => {
			return plugin.tabKey && supportedPluginKeys.includes(plugin.tabKey);
		});
	}, [plugins]);

	// Use custom plugins if provided, otherwise use default filtered plugins
	const pluginsToUse = linkPickerConfig?.linkPickerPlugins || filteredPlugins;

	const isLoadingPlugins = linkPickerConfig?.linkPickerPlugins ? false : isLoading;

	return (
		<LazyLinkPicker
			plugins={pluginsToUse}
			isLoadingPlugins={isLoadingPlugins}
			url={link.url}
			onSubmit={handleSubmit}
			onCancel={handleCancel}
			hideDisplayText
		/>
	);
};
